/**
 * useContractLedger
 * 
 * Fetches ledger records for a contract to power the Integrity Seal.
 * Read-only, no writes.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type SealStatus = 'integro' | 'pendente' | 'comprometido';

export interface LedgerPeriod {
  sequencia: number;
  periodo_competencia: string;
  numero_boletim: number | null;
  hash_atual: string;
  hash_anterior: string;
  status: string;
  sealStatus: SealStatus;
}

export interface LedgerSummary {
  periods: LedgerPeriod[];
  overallSeal: SealStatus;
  totalPeriods: number;
  validPeriods: number;
  pendingPeriods: number;
  compromisedPeriods: number;
  isLoading: boolean;
}

export function useContractLedger(contratoId: string | null): LedgerSummary {
  const { data, isLoading } = useQuery({
    queryKey: ['contract-ledger', contratoId],
    queryFn: async () => {
      const { data: records, error } = await (supabase as any)
        .from('emp_reconciliation_ledger')
        .select('sequencia, periodo_competencia, numero_boletim, hash_atual, hash_anterior, status')
        .eq('contrato_id', contratoId)
        .neq('status', 'superseded')
        .order('sequencia', { ascending: true });

      if (error) throw error;
      return records ?? [];
    },
    enabled: !!contratoId,
    staleTime: 5 * 60_000,
  });

  if (!data || data.length === 0) {
    return {
      periods: [],
      overallSeal: 'pendente',
      totalPeriods: 0,
      validPeriods: 0,
      pendingPeriods: 0,
      compromisedPeriods: 0,
      isLoading,
    };
  }

  // Evaluate each period
  const periods: LedgerPeriod[] = data.map((r: any, idx: number) => {
    let sealStatus: SealStatus = 'pendente';

    if (r.status === 'fechado' && r.hash_atual && r.hash_atual.length === 64) {
      // Check chain link
      if (idx === 0) {
        sealStatus = r.hash_anterior === 'GENESIS' ? 'integro' : 'comprometido';
      } else {
        sealStatus = r.hash_anterior === data[idx - 1].hash_atual ? 'integro' : 'comprometido';
      }
    } else if (r.status === 'aberto') {
      sealStatus = 'pendente';
    }

    return {
      sequencia: r.sequencia,
      periodo_competencia: r.periodo_competencia,
      numero_boletim: r.numero_boletim,
      hash_atual: r.hash_atual,
      hash_anterior: r.hash_anterior,
      status: r.status,
      sealStatus,
    };
  });

  const validPeriods = periods.filter(p => p.sealStatus === 'integro').length;
  const pendingPeriods = periods.filter(p => p.sealStatus === 'pendente').length;
  const compromisedPeriods = periods.filter(p => p.sealStatus === 'comprometido').length;

  let overallSeal: SealStatus = 'pendente';
  if (compromisedPeriods > 0) overallSeal = 'comprometido';
  else if (validPeriods === periods.length) overallSeal = 'integro';
  else overallSeal = 'pendente';

  return {
    periods,
    overallSeal,
    totalPeriods: periods.length,
    validPeriods,
    pendingPeriods,
    compromisedPeriods,
    isLoading,
  };
}
